/**
 * Browser side of a portal voice call with an ElevenLabs Conversational AI
 * agent. The page gets a short-lived signed WebSocket URL from the KC3 API
 * (the API key never reaches the browser) and talks the ElevenLabs
 * conversation protocol directly:
 *
 *   → conversation_initiation_client_data {dynamic_variables}
 *   → {user_audio_chunk: base64 PCM16 mono 16 kHz}   (every 100 ms)
 *   ← conversation_initiation_metadata · audio · interruption · ping
 *   ← user_transcript · agent_response · client_tool_call
 *   → pong · client_tool_result
 *
 * Mic: AudioContext at the device rate (Firefox refuses a mic source on a
 * context with a different rate) + an AudioWorklet that resamples to 16 kHz.
 * Playback: 16 kHz AudioBuffers scheduled back to back; an interruption stops
 * everything queued.
 */

import { startCallRecorder, type CallRecorder } from './call-recorder.js'

export interface BrowserCallHandlers {
  onStarted: (conversationId: string | null) => void
  onCaption: (role: 'caller' | 'bot', text: string) => void
  onSpeaking: (speaking: boolean) => void
  onLevel: (level: number) => void
  /** Run a KC3 tool; resolve with the JSON result for the agent. */
  onToolCall: (name: string, parameters: Record<string, unknown>) => Promise<unknown>
  onClosed: (reason: string) => void
}

const RATE = 16000

const WORKLET = `
class Kc3Mic16k extends AudioWorkletProcessor {
  constructor() { super(); this.ratio = sampleRate / ${RATE}; this.pos = 0; this.acc = 0; this.n = 0; this.out = new Int16Array(1600); this.len = 0 }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (!ch) return true
    let level = 0
    for (let i = 0; i < ch.length; i++) {
      const v = ch[i]; level = Math.max(level, Math.abs(v))
      // box-filter decimation: average every 'ratio' input samples
      this.acc += v; this.n++; this.pos += 1
      if (this.pos >= this.ratio) {
        this.pos -= this.ratio
        const s = Math.max(-1, Math.min(1, this.acc / this.n)); this.acc = 0; this.n = 0
        this.out[this.len++] = s < 0 ? s * 0x8000 : s * 0x7fff
        if (this.len === this.out.length) { this.port.postMessage({ pcm: this.out.buffer.slice(0), level }, []); this.len = 0; level = 0 }
      }
    }
    return true
  }
}
registerProcessor('kc3-mic-16k', Kc3Mic16k)
`

function toB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(bin)
}

function fromB64(b64: string): Int16Array {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2))
}

export class ElevenBrowserCall {
  private ws: WebSocket | null = null
  private ctx: AudioContext | null = null
  private stream: MediaStream | null = null
  private node: AudioWorkletNode | null = null
  private playing: AudioBufferSourceNode[] = []
  private nextAt = 0
  private muted = false
  private closed = false
  private speakingTimer: ReturnType<typeof setTimeout> | null = null
  private recorder: CallRecorder | null = null

  constructor(private readonly h: BrowserCallHandlers) {}

  async start(signedUrl: string, dynamicVariables: Record<string, string>): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    })
    this.ctx = new AudioContext()
    await this.ctx.resume()
    const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }))
    try { await this.ctx.audioWorklet.addModule(url) } finally { URL.revokeObjectURL(url) }
    const src = this.ctx.createMediaStreamSource(this.stream)
    this.node = new AudioWorkletNode(this.ctx, 'kc3-mic-16k')
    src.connect(this.node)
    this.recorder = startCallRecorder(this.ctx, [this.stream])
    this.node.port.onmessage = (e: MessageEvent<{ pcm: ArrayBuffer; level: number }>) => {
      this.h.onLevel(this.muted ? 0 : e.data.level)
      if (!this.muted && this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ user_audio_chunk: toB64(e.data.pcm) }))
    }

    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(signedUrl)
      this.ws = ws
      const timer = setTimeout(() => reject(new Error('The assistant did not answer.')), 12_000)
      ws.onopen = () => ws.send(JSON.stringify({ type: 'conversation_initiation_client_data', dynamic_variables: dynamicVariables }))
      ws.onerror = () => { clearTimeout(timer); reject(new Error('Could not connect to the assistant.')) }
      ws.onclose = (ev) => { clearTimeout(timer); if (!this.closed) { this.teardown(); this.h.onClosed(ev.reason || 'ended') } }
      ws.onmessage = (ev) => {
        let m: Record<string, unknown>
        try { m = JSON.parse(String(ev.data)) as Record<string, unknown> } catch { return }
        if (m['type'] === 'conversation_initiation_metadata') {
          clearTimeout(timer)
          const meta = (m['conversation_initiation_metadata_event'] ?? {}) as Record<string, unknown>
          this.h.onStarted(typeof meta['conversation_id'] === 'string' ? meta['conversation_id'] : null)
          resolve()
        }
        void this.dispatch(m)
      }
    })
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    this.stream?.getAudioTracks().forEach((t) => { t.enabled = !muted })
  }

  /** Milliseconds of AI audio still queued. */
  pendingMs(): number {
    return this.ctx ? Math.max(0, (this.nextAt - this.ctx.currentTime) * 1000) : 0
  }

  /** Stop recording and return the mixed call audio (call before stop()). */
  async finishRecording(): Promise<Blob | null> {
    const r = this.recorder
    this.recorder = null
    return r ? r.stop() : null
  }

  stop(reason = 'visitor_ended'): void {
    if (this.closed) return
    this.closed = true
    try { this.ws?.close(1000, reason) } catch { /* already closed */ }
    this.teardown()
  }

  private teardown(): void {
    this.closed = true
    this.stopPlayback()
    this.node?.disconnect()
    this.stream?.getTracks().forEach((t) => t.stop())
    void this.ctx?.close().catch(() => undefined)
    this.node = null
    this.stream = null
    this.ctx = null
  }

  private stopPlayback(): void {
    for (const s of this.playing) { try { s.stop() } catch { /* already ended */ } }
    this.playing = []
    this.nextAt = 0
    this.h.onSpeaking(false)
  }

  private play(pcm: Int16Array): void {
    if (!this.ctx || pcm.length === 0) return
    const buf = this.ctx.createBuffer(1, pcm.length, RATE)
    const ch = buf.getChannelData(0)
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i]! / 0x8000
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    src.connect(this.ctx.destination)
    if (this.recorder) src.connect(this.recorder.input)
    const at = Math.max(this.ctx.currentTime + 0.05, this.nextAt)
    src.start(at)
    this.nextAt = at + buf.duration
    this.playing.push(src)
    src.onended = () => { this.playing = this.playing.filter((x) => x !== src) }
    this.h.onSpeaking(true)
    if (this.speakingTimer) clearTimeout(this.speakingTimer)
    this.speakingTimer = setTimeout(() => this.h.onSpeaking(false), this.pendingMs() + 150)
  }

  private send(o: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(o))
  }

  private async dispatch(m: Record<string, unknown>): Promise<void> {
    const ev = <T extends Record<string, unknown>>(k: string): T => ((m[k] ?? {}) as T)
    switch (m['type']) {
      case 'audio': {
        const a = ev('audio_event')['audio_base_64']
        if (typeof a === 'string') this.play(fromB64(a))
        break
      }
      case 'interruption':
        this.stopPlayback()
        break
      case 'ping':
        this.send({ type: 'pong', event_id: ev('ping_event')['event_id'] })
        break
      case 'user_transcript': {
        const t = String(ev('user_transcription_event')['user_transcript'] ?? '').trim()
        if (t) this.h.onCaption('caller', t)
        break
      }
      case 'agent_response': {
        const t = String(ev('agent_response_event')['agent_response'] ?? '').trim()
        if (t) this.h.onCaption('bot', t)
        break
      }
      case 'client_tool_call': {
        const c = ev<{ tool_name?: string; tool_call_id?: string; parameters?: Record<string, unknown> }>('client_tool_call')
        let result: unknown
        let isError = false
        try {
          result = await this.h.onToolCall(String(c.tool_name ?? ''), c.parameters ?? {})
        } catch {
          result = { ok: false, error: 'TOOL_FAILED' }
          isError = true
        }
        this.send({ type: 'client_tool_result', tool_call_id: c.tool_call_id, result: JSON.stringify(result), is_error: isError })
        break
      }
      default:
        break
    }
  }
}
